// client/src/pages/AIReview/AIReviewPage.tsx
// /ai-review — AI Tax Return Review dashboard.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Plus, Edit2, Copy, ToggleLeft, ToggleRight, Eye, RefreshCw,
  Play, AlertTriangle, Clock, CheckCircle, ChevronDown, ChevronRight, Terminal, Zap,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { usePermissions } from '../../contexts/PermissionsContext';
import { PERMISSIONS } from '../../types/permissions';
import {
  listProcesses, listRuns, listResults, getResult, saveProcess, triggerProcess, triggerOnDemand, getRunLog,
  listFailures, retryReview,
  AIReviewProcess, AIReviewRun, AIReviewResultMeta, AIReviewResult, AIReviewFailure,
} from '../../services/aiReview';
import AIReviewProcessForm from './AIReviewProcessForm';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = diffMs / 60000;
  if (diffMin < 2)   return 'just now';
  if (diffMin < 60)  return `${Math.round(diffMin)}m ago`;
  if (diffMin < 1440) return `${Math.round(diffMin / 60)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateFull(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function fmtDuration(start: string, end: string | null) {
  if (!end) return '—';
  const s = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function fmtCost(n: number | null | undefined) {
  if (!n) return '—';
  return n < 0.01 ? '<$0.01' : `$${n.toFixed(3)}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AIReviewPage() {
  const perms = usePermissions();
  const { isAdmin, hasPermission, ready } = perms;

  const [processes,    setProcesses]    = useState<AIReviewProcess[]>([]);
  const [loadingProcs, setLoadingProcs] = useState(true);
  const [formTarget,   setFormTarget]   = useState<AIReviewProcess | null | undefined>(undefined);
  const [cloneSource, setCloneSource] = useState<AIReviewProcess | null>(null);
  const [selectedProc, setSelectedProc] = useState<AIReviewProcess | null>(null);
  const [rightTab,     setRightTab]     = useState<'runs' | 'results' | 'console' | 'ondemand' | 'retry'>('runs');
  const [consoleDate,    setConsoleDate]    = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [consoleText,    setConsoleText]    = useState<string | null>(null);
  const [consoleLoading, setConsoleLoading] = useState(false);

  // Run history — all runs on load, filtered when a process is selected
  const [allRuns,     setAllRuns]     = useState<AIReviewRun[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [expandedRun, setExpandedRun] = useState<number | null>(null);

  // Results (blob viewer — only loaded when tab is opened for a selected process)
  const [results,     setResults]     = useState<AIReviewResultMeta[]>([]);
  const [loadingRes,  setLoadingRes]  = useState(false);
  const [viewResult,  setViewResult]  = useState<AIReviewResult | null>(null);
  const [loadingView, setLoadingView] = useState(false);

  const [triggering,  setTriggering]  = useState<number | null>(null);

  // On Demand tab state
  const [odQuery,          setOdQuery]          = useState('');
  const [odClients,        setOdClients]        = useState<{ clientCode: string; clientName: string }[]>([]);
  const [odClientLoading,  setOdClientLoading]  = useState(false);
  const [odSelected,       setOdSelected]       = useState<{ clientCode: string; clientName: string } | null>(null);
  const [odApplyFilter,    setOdApplyFilter]    = useState(false);
  const [odEmailInput,     setOdEmailInput]     = useState('');  // comma-separated raw input
  const [odRunning,        setOdRunning]        = useState(false);
  const [odResult,         setOdResult]         = useState<string | null>(null);
  const odDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Retry tab state
  const [retryFailures,    setRetryFailures]    = useState<AIReviewFailure[]>([]);
  const [retryLoading,     setRetryLoading]     = useState(false);
  const [retryingIdx,      setRetryingIdx]      = useState<number | null>(null);
  const [retryResults,     setRetryResults]     = useState<Record<number, string>>({});

  // Debug console modal state
  const [debugOpen,       setDebugOpen]       = useState(false);
  const [debugLines,      setDebugLines]      = useState<string[]>([]);
  const [debugComplete,   setDebugComplete]   = useState(false);
  const [debugProcId,     setDebugProcId]     = useState<number | null>(null);
  const debugPollRef      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debugScrollRef    = useRef<HTMLDivElement | null>(null);
  const debugCompleteRef  = useRef(false);

  // Auto-scroll debug console to bottom when new lines arrive
  useEffect(() => {
    if (debugScrollRef.current) {
      debugScrollRef.current.scrollTop = debugScrollRef.current.scrollHeight;
    }
  }, [debugLines]);

  const loadProcesses = useCallback(async () => {
    setLoadingProcs(true);
    try   { setProcesses(await listProcesses()); }
    catch (e: any) { toast.error('Failed to load processes: ' + e.message); }
    finally { setLoadingProcs(false); }
  }, []);

  const loadAllRuns = useCallback(async () => {
    setLoadingRuns(true);
    try   { setAllRuns(await listRuns()); }
    catch (e: any) { toast.error('Failed to load run history: ' + e.message); }
    finally { setLoadingRuns(false); }
  }, []);

  useEffect(() => {
    loadProcesses();
    loadAllRuns();
    return () => { stopDebugPoll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadProcesses, loadAllRuns]);

  const searchOdClients = useCallback(async (q: string) => {
    if (q.length < 3) { setOdClients([]); return; }
    setOdClientLoading(true);
    try {
      const res  = await fetch(`/api/pe-client-search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setOdClients((data.clients ?? []).map((c: any) => ({ clientCode: c.clientCode, clientName: c.clientName })));
    } catch { setOdClients([]); }
    finally   { setOdClientLoading(false); }
  }, []);

  useEffect(() => {
    if (odDebounceRef.current) clearTimeout(odDebounceRef.current);
    odDebounceRef.current = setTimeout(() => searchOdClients(odQuery), 300);
  }, [odQuery, searchOdClients]);

  function stopDebugPoll() {
    if (debugPollRef.current) { clearTimeout(debugPollRef.current); debugPollRef.current = null; }
  }

  function startDebugPoll(procId: number) {
    debugCompleteRef.current = false;
    const poll = async () => {
      try {
        const text = await getRunLog(procId, new Date().toISOString().slice(0, 10), true);
        const lines = text.split('\n');
        setDebugLines(lines);
        // Auto-scroll to bottom
        if (debugScrollRef.current) {
          debugScrollRef.current.scrollTop = debugScrollRef.current.scrollHeight;
        }
        const isComplete = lines.some(l => l.includes('=== RUN COMPLETE'));
        if (isComplete && !debugCompleteRef.current) {
          debugCompleteRef.current = true;
          setDebugComplete(true);
          // Auto-collapse after 10 seconds
          setTimeout(() => {
            setDebugOpen(false);
            setDebugComplete(false);
            setDebugLines([]);
          }, 10000);
          return; // Stop polling
        }
      } catch { /* ignore poll errors */ }
      if (!debugCompleteRef.current) {
        debugPollRef.current = setTimeout(poll, 3000);
      }
    };
    debugPollRef.current = setTimeout(poll, 2000); // Initial poll after 2s
  }

  async function handleOnDemandRun() {
    if (!selectedProc || !odSelected) return;
    const extraRecipients = odEmailInput
      .split(/[\s,;]+/)
      .map(e => e.trim())
      .filter(e => e.includes('@'));
    setOdRunning(true);
    setOdResult(null);
    try {
      const res = await triggerOnDemand(selectedProc.id, odSelected.clientCode, odApplyFilter, extraRecipients);
      setOdResult(res.message || `Started — ${(res as any).queued ?? 0} job(s) processing. Check Run History or Console for progress.`);
      // Open debug modal if DEBUG_MODE is active on the server
      if (res.debugMode && res.runId !== undefined) {
        stopDebugPoll();
        setDebugLines([]);
        setDebugComplete(false);
        setDebugProcId(selectedProc.id);
        setDebugOpen(true);
        startDebugPoll(selectedProc.id);
      }
      setTimeout(() => Promise.all([loadProcesses(), loadAllRuns()]), 1500);
    } catch (err: any) {
      setOdResult(`Error: ${err.message}`);
    } finally {
      setOdRunning(false);
    }
  }

  // Runs shown in the right panel — all, or filtered to selected process
  const visibleRuns = selectedProc
    ? allRuns.filter(r => r.process_id === selectedProc.id)
    : allRuns;

  function selectProc(proc: AIReviewProcess) {
    if (selectedProc?.id === proc.id) {
      setSelectedProc(null);
    } else {
      setSelectedProc(proc);
      setViewResult(null);
      setRightTab('runs');
      setConsoleText(null);
      setOdQuery(''); setOdClients([]); setOdSelected(null); setOdEmailInput(''); setOdResult(null);
      setRetryFailures([]); setRetryResults({});
    }
    setExpandedRun(null);
  }

  function switchTab(tab: 'runs' | 'results' | 'console' | 'ondemand' | 'retry') {
    setRightTab(tab);
    if (tab === 'results' && selectedProc && results.length === 0 && !loadingRes) {
      loadResults(selectedProc);
    }
    if (tab === 'console' && selectedProc) {
      loadConsole(selectedProc.id, consoleDate);
    }
    if (tab === 'retry' && selectedProc) {
      loadRetryFailures(selectedProc.id);
    }
  }

  async function loadRetryFailures(procId: number) {
    setRetryLoading(true);
    setRetryFailures([]);
    setRetryResults({});
    try { setRetryFailures(await listFailures(procId)); }
    catch (e: any) { toast.error('Failed to load failures: ' + e.message); }
    finally { setRetryLoading(false); }
  }

  async function handleRetry(failure: AIReviewFailure, action: 'resend' | 'rerun') {
    setRetryingIdx(failure.jobIdx);
    try {
      const res = await retryReview(failure.processId ?? selectedProc!.id, failure.jobIdx, action, failure.failDate);
      setRetryResults(prev => ({ ...prev, [failure.jobIdx]: res.message || 'Done.' }));
    } catch (e: any) {
      setRetryResults(prev => ({ ...prev, [failure.jobIdx]: `Error: ${e.message}` }));
    } finally {
      setRetryingIdx(null);
    }
  }

  async function loadConsole(procId: number, date: string) {
    setConsoleLoading(true);
    setConsoleText(null);
    try {
      setConsoleText(await getRunLog(procId, date));
    } catch (e: any) {
      setConsoleText(`Error loading log: ${e.message}`);
    } finally {
      setConsoleLoading(false);
    }
  }

  const loadResults = useCallback(async (proc: AIReviewProcess) => {
    setLoadingRes(true);
    setResults([]);
    setViewResult(null);
    try   { setResults(await listResults(proc.id)); }
    catch (e: any) { toast.error('Failed to load results: ' + e.message); }
    finally { setLoadingRes(false); }
  }, []);

  async function handleTrigger(proc: AIReviewProcess, e: React.MouseEvent) {
    e.stopPropagation();
    setTriggering(proc.id);
    try {
      const res = await triggerProcess(proc.id);
      toast.info(res.message || `"${proc.name}" — ${(res as any).queued ?? 0} job(s) started. Check Run History for results.`);
      // Refresh after a short delay to pick up the new in-progress run row
      setTimeout(() => Promise.all([loadProcesses(), loadAllRuns()]), 1500);
    } catch (err: any) {
      toast.error('Trigger failed: ' + err.message);
    } finally {
      setTriggering(null);
    }
  }

  async function toggleActive(proc: AIReviewProcess, e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await saveProcess({ ...proc, is_active: !proc.is_active });
      toast.success(proc.is_active ? 'Process paused.' : 'Process activated.');
      loadProcesses();
    } catch (err: any) {
      toast.error(err.message);
    }
  }

  async function handleViewResult(meta: AIReviewResultMeta) {
    if (!selectedProc) return;
    setLoadingView(true);
    setViewResult(null);
    try   { setViewResult(await getResult(selectedProc.id, meta.jobIdx)); }
    catch (e: any) { toast.error('Failed to load result: ' + e.message); }
    finally { setLoadingView(false); }
  }

  if (ready && !hasPermission(PERMISSIONS.NAV_AI_REVIEW)) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1280 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>AI Return Review</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 13 }}>
            Automated GPT-4 quality review of tax returns triggered by PE work status changes.
          </p>
        </div>
        {isAdmin && (
          <button style={primaryBtn} onClick={() => setFormTarget(null)}>
            <Plus size={15} style={{ marginRight: 6 }} /> New Process
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20, alignItems: 'start' }}>

        {/* ── Left panel ── */}
        <div>
          <div style={panel}>
            <div style={panelHeader}>
              <span style={panelTitle}>Review Processes</span>
              <button style={iconBtn} onClick={loadProcesses} title="Refresh">
                <RefreshCw size={14} />
              </button>
            </div>

            {loadingProcs ? (
              <div style={emptyState}>Loading…</div>
            ) : processes.length === 0 ? (
              <div style={emptyState}>
                No processes configured.{isAdmin && ' Click "New Process" to create one.'}
              </div>
            ) : (
              processes.map(proc => (
                <ProcessCard
                  key={proc.id}
                  proc={proc}
                  isSelected={selectedProc?.id === proc.id}
                  isAdmin={isAdmin}
                  triggering={triggering}
                  onClick={() => selectProc(proc)}
                  onTrigger={e => handleTrigger(proc, e)}
                  onEdit={e => { e.stopPropagation(); setCloneSource(null); setFormTarget(proc); }}
                  onClone={e => { e.stopPropagation(); setCloneSource(proc); setFormTarget(null); }}
                  onToggle={e => toggleActive(proc, e)}
                />
              ))
            )}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div>
          {viewResult ? (
            <div style={panel}>
              <div style={panelHeader}>
                <div>
                  <div style={panelTitle}>{viewResult.clientName}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                    {viewResult.jobName} · Reviewed {fmtDate(viewResult.reviewedAt)} ·{' '}
                    {viewResult.inputTokens.toLocaleString()} tokens · {fmtCost(viewResult.estimatedCost)}
                  </div>
                </div>
                <button style={secondaryBtn} onClick={() => setViewResult(null)}>← Back</button>
              </div>
              <div style={{ padding: '20px 24px' }}>
                <ReviewContent content={viewResult.content} />
              </div>
            </div>
          ) : (
            <div style={panel}>
              {/* Panel header */}
              <div style={{ borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px 0 16px' }}>
                  <div>
                    <span style={panelTitle}>
                      {selectedProc ? selectedProc.name : 'All Run History'}
                    </span>
                    {selectedProc && (
                      <button onClick={() => { setSelectedProc(null); setExpandedRun(null); }}
                        style={{ marginLeft: 10, fontSize: 11, color: 'var(--muted)', background: 'none',
                                 border: 'none', cursor: 'pointer', padding: 0 }}>
                        ← All processes
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={iconBtn} title="Refresh"
                      onClick={() => {
                        if (rightTab === 'runs') loadAllRuns();
                        else if (rightTab === 'results' && selectedProc) loadResults(selectedProc);
                        else if (rightTab === 'console' && selectedProc) loadConsole(selectedProc.id, consoleDate);
                      }}>
                      <RefreshCw size={14} />
                    </button>
                    {isAdmin && selectedProc && (
                      <button
                        style={{ ...secondaryBtn, display: 'flex', alignItems: 'center', gap: 5,
                                 opacity: triggering !== null ? 0.5 : 1 }}
                        disabled={triggering !== null}
                        onClick={e => handleTrigger(selectedProc, e)}
                      >
                        {triggering === selectedProc.id
                          ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />
                          : <Play size={13} color="var(--accent)" />}
                        {triggering === selectedProc.id ? 'Running…' : 'Trigger Now'}
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', padding: '0 16px' }}>
                  <button onClick={() => switchTab('runs')}
                    style={{ ...tabBtn, ...(rightTab === 'runs' ? tabBtnActive : {}) }}>
                    Run History
                  </button>
                  {selectedProc && (
                    <button onClick={() => switchTab('results')}
                      style={{ ...tabBtn, ...(rightTab === 'results' ? tabBtnActive : {}) }}>
                      Browse Results
                    </button>
                  )}
                  {selectedProc && (
                    <button onClick={() => switchTab('console')}
                      style={{ ...tabBtn, ...(rightTab === 'console' ? tabBtnActive : {}) }}>
                      <Terminal size={12} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                      Console
                    </button>
                  )}
                  {selectedProc && isAdmin && (
                    <button onClick={() => switchTab('ondemand')}
                      style={{ ...tabBtn, ...(rightTab === 'ondemand' ? tabBtnActive : {}) }}>
                      <Zap size={12} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                      On Demand
                    </button>
                  )}
                  {selectedProc && isAdmin && (
                    <button onClick={() => switchTab('retry')}
                      style={{ ...tabBtn, ...(rightTab === 'retry' ? tabBtnActive : {}) }}>
                      <AlertTriangle size={12} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                      Retry
                      {retryFailures.length > 0 && (
                        <span style={{
                          marginLeft: 5, fontSize: 10, padding: '1px 5px', borderRadius: 8,
                          background: 'rgba(248,81,73,.2)', color: '#f85149',
                          border: '1px solid rgba(248,81,73,.4)', fontWeight: 700,
                        }}>{retryFailures.length}</span>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Tab: Run History */}
              {rightTab === 'runs' && (
                loadingRuns ? (
                  <div style={emptyState}>Loading run history…</div>
                ) : visibleRuns.length === 0 ? (
                  <div style={emptyState}>
                    {selectedProc
                      ? `No runs yet for "${selectedProc.name}".${isAdmin ? ' Use "Trigger Now" or wait for the scheduler.' : ''}`
                      : 'No runs recorded yet. Trigger a process manually or wait for the scheduler.'}
                  </div>
                ) : (
                  <div>
                    {/* Column header */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '28px 1fr 80px 180px 72px 60px',
                      padding: '6px 14px',
                      borderBottom: '1px solid var(--border)',
                      background: 'var(--surface)',
                    }}>
                      {['', selectedProc ? 'When' : 'Process / When', 'Duration', 'Results', 'Cost', ''].map((h, i) => (
                        <div key={i} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)',
                                              textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
                      ))}
                    </div>
                    {visibleRuns.map(run => (
                      <RunRow
                        key={run.id}
                        run={run}
                        showProcess={!selectedProc}
                        expanded={expandedRun === run.id}
                        onToggle={() => setExpandedRun(expandedRun === run.id ? null : run.id)}
                      />
                    ))}
                  </div>
                )
              )}

              {/* Tab: Console (blob logs per day) */}
              {rightTab === 'console' && selectedProc && (
                <div>
                  {/* Date navigation */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 14px', borderBottom: '1px solid var(--border)',
                    background: 'var(--surface)',
                  }}>
                    <button style={iconBtn} title="Previous day" onClick={() => {
                      const d = new Date(consoleDate);
                      d.setUTCDate(d.getUTCDate() - 1);
                      const prev = d.toISOString().slice(0, 10);
                      setConsoleDate(prev);
                      loadConsole(selectedProc.id, prev);
                    }}>←</button>
                    <input
                      type="date"
                      value={consoleDate}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={e => { setConsoleDate(e.target.value); loadConsole(selectedProc.id, e.target.value); }}
                      style={{
                        background: 'var(--input-bg, var(--surface))', color: 'var(--text)',
                        border: '1px solid var(--border)', borderRadius: 4,
                        padding: '3px 8px', fontSize: 12, cursor: 'pointer',
                      }}
                    />
                    <button style={iconBtn} title="Next day" onClick={() => {
                      const d = new Date(consoleDate);
                      d.setUTCDate(d.getUTCDate() + 1);
                      const next = d.toISOString().slice(0, 10);
                      const today = new Date().toISOString().slice(0, 10);
                      if (next > today) return;
                      setConsoleDate(next);
                      loadConsole(selectedProc.id, next);
                    }}>→</button>
                    <button style={{ ...iconBtn, marginLeft: 4 }} title="Refresh"
                      onClick={() => loadConsole(selectedProc.id, consoleDate)}>
                      <RefreshCw size={13} />
                    </button>
                    <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto' }}>
                      Logs stored per process per day · auto-appended each run
                    </span>
                  </div>

                  {/* Log body */}
                  {consoleLoading ? (
                    <div style={emptyState}>Loading…</div>
                  ) : (
                    <div style={{
                      fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
                      fontSize: 12, lineHeight: 1.6,
                      background: '#0d1117',
                      padding: '12px 16px',
                      maxHeight: 520, overflowY: 'auto',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                    }}>
                      {(consoleText || '').split('\n').map((line, i) => {
                        let color = '#c9d1d9';
                        if (line.includes('FAILED') || line.includes('unhandled error')) color = '#f85149';
                        else if (line.includes('SKIPPED')) color = '#8b949e';
                        else if (line.includes('=== RUN COMPLETE')) color = '#3fb950';
                        else if (line.startsWith('=== RUN')) color = '#58a6ff';
                        return <div key={i} style={{ color }}>{line || '\u200b'}</div>;
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Tab: On Demand */}
              {rightTab === 'ondemand' && selectedProc && (
                <div style={{ padding: '20px 24px', maxWidth: 520 }}>
                  <div style={{ marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Client</div>
                  <div style={{ marginBottom: 4, fontSize: 12, color: 'var(--muted)' }}>
                    Type to search — runs this process for the selected client only, skipping dedup.
                  </div>

                  {/* Client search input */}
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Search by name or code…"
                      value={odSelected ? `${odSelected.clientName} (${odSelected.clientCode})` : odQuery}
                      onChange={e => { setOdSelected(null); setOdQuery(e.target.value); setOdResult(null); }}
                      style={{
                        width: '100%', padding: '8px 11px', borderRadius: 8,
                        border: '1px solid var(--border)', background: 'rgba(0,0,0,.18)',
                        color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                    {odSelected && (
                      <button
                        onClick={() => { setOdSelected(null); setOdQuery(''); setOdResult(null); }}
                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                                 background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14 }}
                      >✕</button>
                    )}
                  </div>

                  {/* Dropdown results */}
                  {!odSelected && odQuery.length >= 3 && (
                    <div style={{
                      border: '1px solid var(--border)', borderRadius: 8, marginTop: 4,
                      background: 'var(--surface)', maxHeight: 220, overflowY: 'auto',
                    }}>
                      {odClientLoading ? (
                        <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--muted)' }}>Searching…</div>
                      ) : odClients.length === 0 ? (
                        <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--muted)' }}>No results</div>
                      ) : odClients.map(c => (
                        <div
                          key={c.clientCode}
                          onClick={() => { setOdSelected(c); setOdQuery(''); setOdClients([]); }}
                          style={{
                            padding: '9px 14px', fontSize: 13, cursor: 'pointer',
                            borderBottom: '1px solid var(--border)',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.04)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                          <span style={{ fontWeight: 600 }}>{c.clientName}</span>
                          <span style={{ color: 'var(--muted)', marginLeft: 8, fontSize: 12 }}>{c.clientCode}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Status filter toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
                    <button
                      onClick={() => setOdApplyFilter(v => !v)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      {odApplyFilter
                        ? <ToggleRight size={22} color="var(--accent)" />
                        : <ToggleLeft  size={22} color="var(--muted)" />}
                    </button>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>Apply work status filter</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {odApplyFilter
                          ? `Only jobs with status "${selectedProc.pe_work_status}" will run`
                          : 'Run regardless of current job work status'}
                      </div>
                    </div>
                  </div>

                  {/* Extra recipients */}
                  <div style={{ marginTop: 18 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>Additional recipients <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optional)</span></div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                      Added on top of the process's normal recipient list. Separate multiple addresses with commas.
                    </div>
                    <input
                      type="text"
                      placeholder="e.g. jsmith@bmss.com, mlee@bmss.com"
                      value={odEmailInput}
                      onChange={e => setOdEmailInput(e.target.value)}
                      style={{
                        width: '100%', padding: '8px 11px', borderRadius: 8,
                        border: '1px solid var(--border)', background: 'rgba(0,0,0,.18)',
                        color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {/* Run button */}
                  <button
                    disabled={!odSelected || odRunning}
                    onClick={handleOnDemandRun}
                    style={{
                      marginTop: 20, padding: '9px 20px', borderRadius: 8,
                      border: '1px solid rgba(122,162,255,.65)', background: 'rgba(122,162,255,.18)',
                      color: 'var(--accent)', fontWeight: 600, fontSize: 13, cursor: odSelected && !odRunning ? 'pointer' : 'not-allowed',
                      opacity: !odSelected || odRunning ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit',
                    }}
                  >
                    {odRunning
                      ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Running…</>
                      : <><Zap size={13} /> Run Now</>}
                  </button>

                  {/* Result */}
                  {odResult && (
                    <div style={{
                      marginTop: 14, padding: '10px 14px', borderRadius: 8,
                      border: `1px solid ${odResult.startsWith('Error') ? 'rgba(248,81,73,.4)' : 'rgba(63,185,80,.4)'}`,
                      background: odResult.startsWith('Error') ? 'rgba(248,81,73,.08)' : 'rgba(63,185,80,.08)',
                      fontSize: 13,
                      color: odResult.startsWith('Error') ? '#f85149' : '#3fb950',
                    }}>
                      {odResult}
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Retry (PDF failures) */}
              {rightTab === 'retry' && selectedProc && (
                <div>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', borderBottom: '1px solid var(--border)',
                    background: 'var(--surface)',
                  }}>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                      Jobs where PDF generation failed — analysis was saved and can be re-sent or re-run.
                    </span>
                    <button style={iconBtn} title="Refresh" onClick={() => loadRetryFailures(selectedProc.id)}>
                      <RefreshCw size={13} />
                    </button>
                  </div>

                  {retryLoading ? (
                    <div style={emptyState}>Loading…</div>
                  ) : retryFailures.length === 0 ? (
                    <div style={emptyState}>No PDF failures recorded for this process.</div>
                  ) : retryFailures.map(f => (
                    <div key={`${f.jobIdx}-${f.failDate}`} style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--border)',
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: 12,
                      alignItems: 'start',
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {f.clientName || f.clientCode}
                          {f.clientCode && f.clientName && (
                            <span style={{ color: 'var(--muted)', fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
                              {f.clientCode}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                          {f.jobName} · {f.taxyear} · Failed {f.failDate}
                          {f.model && <> · <span style={{ color: 'var(--text)' }}>{f.model}</span></>}
                        </div>
                        {f.error && (
                          <div style={{
                            marginTop: 6, fontSize: 11, color: '#f85149',
                            background: 'rgba(248,81,73,.07)', border: '1px solid rgba(248,81,73,.25)',
                            borderRadius: 4, padding: '4px 8px',
                            fontFamily: 'monospace', wordBreak: 'break-all',
                          }}>
                            {f.error}
                          </div>
                        )}
                        {retryResults[f.jobIdx] && (
                          <div style={{
                            marginTop: 6, fontSize: 12,
                            color: retryResults[f.jobIdx].startsWith('Error') ? '#f85149' : '#3fb950',
                          }}>
                            {retryResults[f.jobIdx]}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button
                          disabled={retryingIdx !== null}
                          onClick={() => handleRetry(f, 'resend')}
                          title="Re-generate PDF and send email using the saved analysis"
                          style={{
                            ...secondaryBtn,
                            opacity: retryingIdx !== null ? 0.5 : 1,
                            cursor: retryingIdx !== null ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 5,
                          }}
                        >
                          {retryingIdx === f.jobIdx
                            ? <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />
                            : <Play size={12} color="var(--accent)" />}
                          Re-send
                        </button>
                        <button
                          disabled={retryingIdx !== null}
                          onClick={() => handleRetry(f, 'rerun')}
                          title="Run a full new AI analysis and send fresh results"
                          style={{
                            ...secondaryBtn,
                            opacity: retryingIdx !== null ? 0.5 : 1,
                            cursor: retryingIdx !== null ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 5,
                          }}
                        >
                          <Zap size={12} color="#e3b341" />
                          Re-run
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Tab: Browse Results (only available when a process is selected) */}
              {rightTab === 'results' && selectedProc && (
                loadingRes ? (
                  <div style={emptyState}>Loading results…</div>
                ) : results.length === 0 ? (
                  <div style={emptyState}>No results stored yet for this process.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {['Job #', 'Review Date', 'Size', ''].map(h => (
                          <th key={h} style={thStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.map(r => (
                        <tr key={r.blobName} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '9px 14px', fontWeight: 600 }}>{r.jobIdx}</td>
                          <td style={{ padding: '9px 14px' }}>{r.reviewDate}</td>
                          <td style={{ padding: '9px 14px', color: 'var(--muted)' }}>
                            {r.sizeBytes > 1024 ? `${(r.sizeBytes / 1024).toFixed(1)} KB` : `${r.sizeBytes} B`}
                          </td>
                          <td style={{ padding: '9px 14px', textAlign: 'right' }}>
                            <button style={viewBtn} onClick={() => handleViewResult(r)}>
                              {loadingView ? '…' : <><Eye size={13} style={{ marginRight: 5 }} />View</>}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {/* Process form modal */}
      {formTarget !== undefined && (
        <AIReviewProcessForm
          existing={formTarget}
          cloneOf={cloneSource}
          allProcesses={processes.map(p => ({ id: p.id, name: p.name }))}
          onClose={() => { setFormTarget(undefined); setCloneSource(null); }}
          onSaved={() => { setFormTarget(undefined); setCloneSource(null); loadProcesses(); }}
        />
      )}

      {/* Debug console modal */}
      {debugOpen && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
          width: 620, maxWidth: 'calc(100vw - 48px)',
          background: '#0d1117',
          border: '1px solid #30363d',
          borderRadius: 10,
          boxShadow: '0 8px 32px rgba(0,0,0,.6)',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 14px',
            borderBottom: '1px solid #21262d',
            background: '#161b22',
            borderRadius: '10px 10px 0 0',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Terminal size={13} color="#58a6ff" />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#c9d1d9', fontFamily: 'monospace' }}>
                Debug Console — {selectedProc?.name ?? `Process ${debugProcId}`}
              </span>
              {debugComplete ? (
                <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: '#3fb95022', color: '#3fb950', border: '1px solid #3fb95044', fontWeight: 600 }}>
                  Complete · closing in 10s
                </span>
              ) : (
                <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: '#58a6ff22', color: '#58a6ff', border: '1px solid #58a6ff44', fontWeight: 600 }}>
                  Live
                </span>
              )}
            </div>
            <button
              onClick={() => { stopDebugPoll(); setDebugOpen(false); setDebugLines([]); setDebugComplete(false); }}
              style={{ background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
              title="Close"
            >✕</button>
          </div>

          {/* Log body */}
          <div
            ref={debugScrollRef}
            style={{
              fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
              fontSize: 11, lineHeight: 1.6,
              padding: '10px 14px',
              maxHeight: 340, overflowY: 'auto',
              color: '#c9d1d9',
            }}
          >
            {debugLines.length === 0 ? (
              <span style={{ color: '#8b949e', fontStyle: 'italic' }}>Waiting for first log entry…</span>
            ) : debugLines.map((line, i) => {
              let color = '#c9d1d9';
              if (line.includes('FAILED') || line.includes('unhandled error')) color = '#f85149';
              else if (line.includes('SKIPPED')) color = '#8b949e';
              else if (line.includes('=== RUN COMPLETE')) color = '#3fb950';
              else if (line.startsWith('=== LIVE RUN') || line.startsWith('=== RUN')) color = '#58a6ff';
              else if (line.includes('Starting:')) color = '#e3b341';
              else if (line.includes('Reviewed:')) color = '#7ee787';
              return <div key={i} style={{ color }}>{line || '\u200b'}</div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── ProcessCard ────────────────────────────────────────────────────────────────

interface ProcessCardProps {
  proc: AIReviewProcess;
  isSelected: boolean;
  isAdmin: boolean;
  triggering: number | null;
  onClick: () => void;
  onTrigger: (e: React.MouseEvent) => void;
  onEdit: (e: React.MouseEvent) => void;
  onClone: (e: React.MouseEvent) => void;
  onToggle: (e: React.MouseEvent) => void;
}

function ProcessCard({ proc, isSelected, isAdmin, triggering, onClick, onTrigger, onEdit, onClone, onToggle }: ProcessCardProps) {
  const hasLastRun = proc.last_run_at != null;
  const hasFailed  = (proc.last_failed ?? 0) > 0;

  return (
    <div onClick={onClick} style={{
      padding: '12px 14px',
      borderBottom: '1px solid var(--border)',
      cursor: 'pointer',
      background: isSelected ? 'var(--surface)' : 'transparent',
      borderLeft: isSelected ? '3px solid var(--accent)' : '3px solid transparent',
      transition: 'background 0.1s',
    }}>
      {/* Row 1: name + badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 8 }}>
          {proc.name}
        </span>
        <span style={{
          fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 600, whiteSpace: 'nowrap',
          background: proc.is_active ? '#9ece6a22' : '#9aa5ce22',
          color: proc.is_active ? '#9ece6a' : '#9aa5ce',
          border: `1px solid ${proc.is_active ? '#9ece6a44' : '#9aa5ce44'}`,
        }}>
          {proc.is_active ? 'Active' : 'Paused'}
        </span>
      </div>

      {/* Row 2: config summary */}
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
        {proc.pe_work_status ?? '—'} · {proc.model} · {proc.export_scope}
      </div>

      {/* Row 3: last run stats + action buttons */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 11, color: hasFailed ? '#f7768e' : 'var(--muted)' }}>
          {hasLastRun ? (
            <>
              {hasFailed && <AlertTriangle size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />}
              {fmtDate(proc.last_run_at!)}
              {' · '}
              <span style={{ color: proc.last_reviewed ? 'var(--text)' : 'var(--muted)' }}>
                {proc.last_reviewed ?? 0} reviewed
              </span>
              {(proc.last_skipped ?? 0) > 0 && `, ${proc.last_skipped} skipped`}
              {hasFailed && `, ${proc.last_failed} failed`}
            </>
          ) : (
            <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>Never run</span>
          )}
        </div>

        {isAdmin && (
          <div style={{ display: 'flex', gap: 2 }} onClick={e => e.stopPropagation()}>
            <button
              title="Trigger Now"
              disabled={triggering !== null}
              style={{ ...iconBtn, opacity: triggering !== null ? 0.4 : 1 }}
              onClick={onTrigger}
            >
              {triggering === proc.id
                ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />
                : <Play size={13} color="#9ece6a" />}
            </button>
            <button style={iconBtn} title="Edit" onClick={onEdit}><Edit2 size={13} /></button>
            <button style={iconBtn} title="Clone" onClick={onClone}><Copy size={13} /></button>
            <button style={iconBtn} title={proc.is_active ? 'Pause' : 'Activate'} onClick={onToggle}>
              {proc.is_active ? <ToggleRight size={15} color="#9ece6a" /> : <ToggleLeft size={15} />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── RunRow ────────────────────────────────────────────────────────────────────

interface RunRowError { jobIdx: number; clientCode: string; clientName: string; error: string; }

function RunRow({ run, showProcess, expanded, onToggle }: {
  run: AIReviewRun;
  showProcess: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const errors: RunRowError[] = (() => {
    try { return run.errors ? JSON.parse(run.errors) : []; } catch { return []; }
  })();

  const hasFailed  = run.failed > 0;
  const allSkipped = run.reviewed === 0 && run.failed === 0 && run.matched > 0;
  const isRunning  = !run.finished_at;

  const statusColor = hasFailed ? '#f7768e' : allSkipped ? 'var(--muted)' : '#9ece6a';
  const StatusIcon  = hasFailed ? AlertTriangle : isRunning ? Clock : CheckCircle;

  return (
    <>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '28px 1fr 80px 180px 72px 60px',
        alignItems: 'center',
        padding: '9px 14px',
        borderBottom: '1px solid var(--border)',
        fontSize: 13,
        background: expanded ? 'var(--surface)' : 'transparent',
        cursor: errors.length > 0 ? 'pointer' : 'default',
      }} onClick={errors.length > 0 ? onToggle : undefined}>

        {/* Status icon */}
        <div>
          <StatusIcon size={14} color={statusColor} />
        </div>

        {/* When + process name (if global view) + trigger badge */}
        <div>
          {showProcess && (
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 1 }}>{run.process_name}</div>
          )}
          <div style={{ fontWeight: showProcess ? 400 : 500, color: showProcess ? 'var(--muted)' : 'var(--text)', fontSize: showProcess ? 11 : 13 }}>
            {fmtDateFull(run.started_at)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
            <span style={{
              display: 'inline-block', padding: '1px 6px', borderRadius: 8, fontSize: 10,
              background: run.run_type === 'ondemand' ? '#e0af6822' : run.run_type === 'manual' ? '#7aa2f722' : '#9aa5ce22',
              color: run.run_type === 'ondemand' ? '#e0af68' : run.run_type === 'manual' ? '#7aa2f7' : '#9aa5ce',
              border: `1px solid ${run.run_type === 'ondemand' ? '#e0af6844' : run.run_type === 'manual' ? '#7aa2f744' : '#9aa5ce44'}`,
            }}>
              {run.run_type === 'ondemand' ? 'On Demand' : run.run_type === 'manual' ? 'Manual' : 'Scheduled'}
            </span>
          </div>
        </div>

        {/* Duration */}
        <div style={{ color: 'var(--muted)', fontSize: 12 }}>
          {isRunning ? (
            <span style={{ color: '#e0af68' }}>Running…</span>
          ) : (
            fmtDuration(run.started_at, run.finished_at)
          )}
        </div>

        {/* Stats */}
        <div style={{ fontSize: 12 }}>
          <span style={{ color: run.reviewed > 0 ? 'var(--text)' : 'var(--muted)' }}>{run.reviewed} reviewed</span>
          {run.skipped > 0 && <span style={{ color: 'var(--muted)' }}> · {run.skipped} skipped</span>}
          {hasFailed && <span style={{ color: '#f7768e' }}> · {run.failed} failed</span>}
          {run.matched === 0 && <span style={{ color: 'var(--muted)' }}>No matching jobs</span>}
        </div>

        {/* Cost */}
        <div style={{ color: 'var(--muted)', fontSize: 12 }}>{fmtCost(run.total_cost)}</div>

        {/* Expand chevron (only when there are errors) */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {errors.length > 0 && (
            expanded
              ? <ChevronDown size={14} color="var(--muted)" />
              : <ChevronRight size={14} color="#f7768e" />
          )}
        </div>
      </div>

      {/* Expanded error detail */}
      {expanded && errors.length > 0 && (
        <div style={{
          background: '#f7768e11',
          borderBottom: '1px solid var(--border)',
          padding: '10px 14px 10px 56px',
          borderLeft: '3px solid #f7768e',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#f7768e', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Failure Details
          </div>
          {errors.map((err, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 12 }}>
                Job #{err.jobIdx} — {err.clientName || err.clientCode}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#f7768e', marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {err.error}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ── ReviewContent ─────────────────────────────────────────────────────────────

const MD_STYLE_ID = 'ai-review-md-styles';
if (typeof document !== 'undefined' && !document.getElementById(MD_STYLE_ID)) {
  const s = document.createElement('style');
  s.id = MD_STYLE_ID;
  s.textContent = `
    .ai-md h1 { font-size: 1.5em; font-weight: 700; border-bottom: 1px solid var(--border); padding-bottom: .25em; margin: 20px 0 10px; }
    .ai-md h2 { font-size: 1.2em; font-weight: 700; border-bottom: 1px solid var(--border); padding-bottom: .2em; color: var(--accent); margin: 18px 0 8px; }
    .ai-md h3 { font-size: 1em; font-weight: 600; margin: 14px 0 6px; }
    .ai-md p  { margin: 0 0 8px; }
    .ai-md ul, .ai-md ol { padding-left: 1.6em; margin: 0 0 10px; }
    .ai-md li { margin: 2px 0; }
    .ai-md table { border-collapse: collapse; width: 100%; margin: 12px 0; font-size: 12.5px; }
    .ai-md th { background: var(--surface); font-weight: 600; text-align: left; }
    .ai-md th, .ai-md td { border: 1px solid var(--border); padding: 6px 10px; }
    .ai-md tr:nth-child(even) td { background: color-mix(in srgb, var(--surface) 60%, transparent); }
    .ai-md code { background: var(--surface); border-radius: 3px; padding: .1em .35em; font-size: 85%; }
    .ai-md pre  { background: var(--surface); border: 1px solid var(--border); border-radius: 5px; padding: 10px 14px; overflow-x: auto; }
    .ai-md pre code { background: none; padding: 0; }
    .ai-md blockquote { border-left: 3px solid var(--border); padding: 0 .8em; color: var(--muted); margin: 0 0 10px; }
    .ai-md hr { border: none; border-top: 1px solid var(--border); margin: 14px 0; }
  `;
  document.head.appendChild(s);
}

function ReviewContent({ content }: { content: string }) {
  return (
    <div className="ai-md" style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--text)' }}>
      <ReactMarkdown>{content}</ReactMarkdown>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const panel: React.CSSProperties = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
};
const panelHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px', borderBottom: '1px solid var(--border)',
};
const panelTitle: React.CSSProperties = { fontWeight: 600, fontSize: 13 };
const emptyState: React.CSSProperties = {
  padding: '36px 24px', textAlign: 'center', color: 'var(--muted)', fontSize: 13,
};
const iconBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)',
  display: 'flex', alignItems: 'center', padding: 4, borderRadius: 4,
};
const primaryBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', background: 'var(--accent)', color: '#fff',
  border: 'none', borderRadius: 5, padding: '8px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
};
const secondaryBtn: React.CSSProperties = {
  background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)',
  borderRadius: 5, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
};
const viewBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', background: 'var(--surface)',
  border: '1px solid var(--border)', borderRadius: 4, padding: '4px 10px',
  fontSize: 12, cursor: 'pointer', color: 'var(--text)',
};
const tabBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', padding: '8px 12px',
  fontSize: 13, color: 'var(--muted)', borderBottom: '2px solid transparent', marginBottom: -1,
};
const tabBtnActive: React.CSSProperties = {
  color: 'var(--accent)', borderBottomColor: 'var(--accent)', fontWeight: 600,
};
const thStyle: React.CSSProperties = {
  padding: '8px 14px', textAlign: 'left', color: 'var(--muted)',
  fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
};
